import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import paths from '@src/paths';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'default' },
  { value: 'medium', label: 'Medium', color: 'info' },
  { value: 'high', label: 'High', color: 'warning' },
  { value: 'critical', label: 'Critical', color: 'error' },
];

const WORK_DECISION_LABELS = {
  no_reply: 'No reply',
  reply_only: 'Reply only',
  reply_and_task: 'Reply + task',
  task_only: 'Task only',
  needs_human_review: 'Needs review',
};

const DUE_DATE_QUICK_PICKS = [
  { label: 'Today', getValue: () => dayjs().format('YYYY-MM-DD') },
  { label: 'Tomorrow', getValue: () => dayjs().add(1, 'day').format('YYYY-MM-DD') },
  { label: 'Next week', getValue: () => dayjs().add(1, 'week').format('YYYY-MM-DD') },
];

function formatCategory(value) {
  return value ? value.replace(/_/g, ' ') : '';
}

function getInitialFormState(initialValues, defaultStatus, defaultAssigneeId) {
  if (!initialValues) {
    return {
      title: '',
      clientId: null,
      // Quick-add default: assign to the logged-in user (clearable).
      assigneeId: defaultAssigneeId ?? null,
      dueDate: '',
      priority: 'medium',
      status: defaultStatus,
      notes: '',
      description: '',
      isCompleted: false,
    };
  }

  return {
    title: initialValues.title || '',
    clientId: initialValues.client || null,
    assigneeId: initialValues.assignee || null,
    dueDate: initialValues.due_date || '',
    priority: initialValues.priority || 'medium',
    status: initialValues.status || defaultStatus,
    notes: initialValues.notes || '',
    description: initialValues.description || '',
    isCompleted: Boolean(initialValues.is_completed),
  };
}

function TaskDialog({
  open,
  onClose,
  onSubmit,
  statusOptions,
  clients,
  assignees,
  initialValues,
  defaultAssigneeId,
}) {
  // New tasks start in the backlog, not the first column (agent_proposed).
  const defaultStatus = statusOptions.some((option) => option.value === 'backlog')
    ? 'backlog'
    : statusOptions[0]?.value || 'backlog';
  const [formState, setFormState] = useState(() => getInitialFormState(initialValues, defaultStatus, defaultAssigneeId));
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const titleInputRef = useRef(null);

  useEffect(() => {
    setFormState(getInitialFormState(initialValues, defaultStatus, defaultAssigneeId));
    setShowErrors(false);
    setSaving(false);
  }, [initialValues, defaultStatus, defaultAssigneeId, open]);

  const clientValue = useMemo(
    () => clients.find((client) => client.id === formState.clientId) || null,
    [clients, formState.clientId],
  );
  const assigneeValue = useMemo(
    () => assignees.find((assignee) => assignee.id === formState.assigneeId) || null,
    [assignees, formState.assigneeId],
  );

  const titleMissing = !formState.title.trim();

  const handleChange = (field) => (event) => {
    setFormState((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = (event, { addAnother = false } = {}) => {
    if (event) {
      event.preventDefault();
    }
    if (saving) {
      return;
    }
    if (titleMissing) {
      setShowErrors(true);
      return;
    }

    setSaving(true);
    const result = onSubmit({
      title: formState.title.trim(),
      clientId: formState.clientId,
      assigneeId: formState.assigneeId,
      dueDate: formState.dueDate || null,
      priority: formState.priority,
      status: formState.status,
      notes: formState.notes,
      description: formState.description,
      isCompleted: formState.isCompleted,
      addAnother,
    });
    if (result && typeof result.then === 'function') {
      result
        .then((saved) => {
          // Batch entry: on success keep client/assignee/priority/due date,
          // clear the per-task text fields, and put the cursor back on Title.
          if (saved && addAnother) {
            setFormState((prev) => ({
              ...prev,
              title: '',
              notes: '',
              description: '',
              isCompleted: false,
            }));
            setShowErrors(false);
            setTimeout(() => titleInputRef.current?.focus(), 0);
          }
        })
        .finally(() => setSaving(false));
    }
  };

  const handleKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const isEdit = Boolean(initialValues);
  const fromInbox = Boolean(initialValues?.source_email_thread_id);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      onKeyDown={handleKeyDown}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: fromInbox || initialValues?.parent ? 0.5 : undefined }}>
          {isEdit ? 'Edit task' : 'Create task'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {initialValues?.parent && (
              <Alert severity="info" sx={{ py: 0 }}>
                Subtask of {initialValues.parent_title || `Task #${initialValues.parent}`}.
              </Alert>
            )}

            {fromInbox ? (
              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" color="info" variant="outlined" label="From client inbox" />
                  {clientValue ? (
                    <Chip size="small" variant="outlined" label={clientValue.name} />
                  ) : null}
                  {initialValues.source_email_work_decision ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        WORK_DECISION_LABELS[initialValues.source_email_work_decision]
                        || initialValues.source_email_work_decision
                      }
                    />
                  ) : null}
                  {initialValues.source_email_router_category ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={formatCategory(initialValues.source_email_router_category)}
                    />
                  ) : null}
                  <Button
                    component={RouterLink}
                    to={`${paths.clientInbox}?thread=${initialValues.source_email_thread_id}`}
                    size="small"
                    sx={{ ml: 'auto' }}
                  >
                    Open thread
                  </Button>
                </Stack>
                <Typography variant="body2" sx={{ mt: 0.75 }} color="text.secondary" noWrap>
                  {initialValues.source_email_subject || 'Client inbox thread'}
                </Typography>
                {initialValues.source_email_summary ? (
                  <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                    {initialValues.source_email_summary}
                  </Typography>
                ) : null}
                {initialValues.source_email_router_reason ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, whiteSpace: 'pre-wrap' }}>
                    Routing: {initialValues.source_email_router_reason}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            <TextField
              label="Title"
              value={formState.title}
              onChange={handleChange('title')}
              autoFocus
              fullWidth
              required
              inputRef={titleInputRef}
              error={showErrors && titleMissing}
              helperText={showErrors && titleMissing ? 'A title is required.' : ' '}
              InputProps={{ sx: { fontSize: '1.05rem', fontWeight: 500 } }}
              FormHelperTextProps={{ sx: { mt: 0.25 } }}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Priority
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={formState.priority}
                  onChange={(_, value) => {
                    if (value) {
                      setFormState((prev) => ({ ...prev, priority: value }));
                    }
                  }}
                >
                  {PRIORITY_OPTIONS.map((option) => (
                    <ToggleButton
                      key={option.value}
                      value={option.value}
                      color={option.color === 'default' ? 'standard' : option.color}
                      sx={{ px: 1.5, textTransform: 'none' }}
                    >
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              <TextField
                select
                label="Status"
                size="small"
                value={formState.status}
                onChange={handleChange('status')}
                sx={{ minWidth: 160, mt: { sm: '18px' } }}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="Due date"
                  type="date"
                  size="small"
                  value={formState.dueDate || ''}
                  onChange={handleChange('dueDate')}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 180 }}
                />
                <Stack direction="row" spacing={0.75}>
                  {DUE_DATE_QUICK_PICKS.map((pick) => {
                    const pickValue = pick.getValue();
                    return (
                      <Chip
                        key={pick.label}
                        label={pick.label}
                        size="small"
                        variant={formState.dueDate === pickValue ? 'filled' : 'outlined'}
                        color={formState.dueDate === pickValue ? 'primary' : 'default'}
                        onClick={() => setFormState((prev) => ({ ...prev, dueDate: pickValue }))}
                      />
                    );
                  })}
                  {formState.dueDate ? (
                    <Chip
                      label="Clear"
                      size="small"
                      variant="outlined"
                      onClick={() => setFormState((prev) => ({ ...prev, dueDate: '' }))}
                    />
                  ) : null}
                </Stack>
              </Stack>
            </Box>

            <Divider />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Autocomplete
                options={clients}
                value={clientValue}
                onChange={(_, value) => setFormState((prev) => ({ ...prev, clientId: value ? value.id : null }))}
                getOptionLabel={(option) => option?.name ?? ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client (optional — leave empty for internal / cross-client)"
                    helperText=" "
                  />
                )}
              />
              <Autocomplete
                options={assignees}
                value={assigneeValue}
                onChange={(_, value) => setFormState((prev) => ({ ...prev, assigneeId: value ? value.id : null }))}
                getOptionLabel={(option) => option?.full_name || option?.email || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} label="Assignee" placeholder="Unassigned" helperText=" " />
                )}
              />
            </Stack>

            <TextField
              label="Action plan / notes"
              value={formState.notes}
              onChange={handleChange('notes')}
              multiline
              minRows={4}
              fullWidth
              helperText="The concrete work the team should perform."
            />
            <TextField
              label="Additional background"
              value={formState.description}
              onChange={handleChange('description')}
              multiline
              minRows={2}
              fullWidth
              helperText="Reference detail, not the core to-do."
            />

            <FormControlLabel
              control={(
                <Switch
                  checked={formState.isCompleted}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      isCompleted: event.target.checked,
                      status:
                        event.target.checked && prev.status === 'completed'
                          ? prev.status
                          : event.target.checked
                          ? 'completed'
                          : prev.status === 'completed'
                          ? defaultStatus
                          : prev.status,
                    }))
                  }
                />
              )}
              label="Mark as completed"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', display: { xs: 'none', sm: 'block' } }}>
            ⌘/Ctrl + Enter to save
          </Typography>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {!isEdit ? (
            <Button
              type="button"
              variant="outlined"
              disabled={saving}
              onClick={(event) => handleSubmit(event, { addAnother: true })}
            >
              Create & add another
            </Button>
          ) : null}
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isEdit ? 'Save changes' : 'Create task'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

TaskDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  assignees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      full_name: PropTypes.string,
      email: PropTypes.string,
    }),
  ).isRequired,
  defaultAssigneeId: PropTypes.number,
  initialValues: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    client: PropTypes.number,
    assignee: PropTypes.number,
    due_date: PropTypes.string,
    priority: PropTypes.string,
    status: PropTypes.string,
    notes: PropTypes.string,
    description: PropTypes.string,
    is_completed: PropTypes.bool,
    parent: PropTypes.number,
    parent_title: PropTypes.string,
    source_email_thread_id: PropTypes.number,
    source_email_subject: PropTypes.string,
    source_email_summary: PropTypes.string,
    source_email_work_decision: PropTypes.string,
    source_email_router_category: PropTypes.string,
    source_email_router_reason: PropTypes.string,
  }),
};

TaskDialog.defaultProps = {
  initialValues: null,
  defaultAssigneeId: null,
};

export default TaskDialog;
